// NK-344 End Client Reporting Package — see frontend/.claude/features/NK-344/spec.md
// ("Report Design Schema") for the design rationale behind this shape.

import { BarChartExtraData } from './end-client-reporting-component-types/bar-chart';
import { FilterComparisonExtraData } from './end-client-reporting-component-types/filter-comparison';
import { DonutChartExtraData } from './end-client-reporting-component-types/donut-chart';
import { HeadlineStatsExtraData } from './end-client-reporting-component-types/headline-stats';
import { FunnelExtraData } from './end-client-reporting-component-types/funnel';
import { CrosstabExtraData } from './end-client-reporting-component-types/crosstab';
import { DivergingBarsExtraData } from './end-client-reporting-component-types/diverging-bars';
import { TieredRankingExtraData } from './end-client-reporting-component-types/tiered-ranking';
import { OpenEndExplorerExtraData } from './end-client-reporting-component-types/open-end-explorer';
import { StageExplainerExtraData } from './end-client-reporting-component-types/stage-explainer';
import { OwnershipMatrixExtraData } from './end-client-reporting-component-types/ownership-matrix';
import { StageMatrixExtraData } from './end-client-reporting-component-types/stage-matrix';
import { ScatterPlotExtraData } from './end-client-reporting-component-types/scatter-plot';
import { WordCloudExtraData } from './end-client-reporting-component-types/word-cloud';
import { HeatmapExtraData } from './end-client-reporting-component-types/heatmap';
import { TextExtraData } from './end-client-reporting-component-types/text';
import { ImageExtraData } from './end-client-reporting-component-types/image';
import { SegmentTilesExtraData } from './end-client-reporting-component-types/segment-tiles';
import { StudyOverviewExtraData } from './end-client-reporting-component-types/study-overview';
import { KpiScoreExtraData } from './end-client-reporting-component-types/kpi-score';
import { ActionListExtraData } from './end-client-reporting-component-types/action-list';
import { StackedScaleExtraData } from './end-client-reporting-component-types/stacked-scale';
import { PieChartExtraData } from './end-client-reporting-component-types/pie-chart';
import { SubgroupPanelsExtraData } from './end-client-reporting-component-types/subgroup-panels';

// The EndClientReport entity (backend/NavigatorAPI/Models/EndClientReport.cs) and its DTO
// (Dtos/EndClientReportDto.cs) -- the report-deck row, not the report-design.json content below.
// Numeric values must match Enums/EndClientReportIngestionStatus.cs and
// EndClientReportStatus.cs exactly: the API serializes enums as plain numbers (System.Text.Json,
// no StringEnumConverter configured), not as strings.
export enum EndClientReportStatus {
  Ingesting = 0,
  Ready = 1,
  Failed = 2,
}

export interface EndClientReport {
  id: string;
  projectId: string;
  name: string;
  description?: string; // shown on the deck list/intro screen; not set until something writes it (create flow, or task #5's generation) -- lives here, not in report-design.json, so listing decks never needs an S3 fetch
  createdByName?: string; // display name, resolved server-side from CreatedBy -- not the raw user id
  status: EndClientReportStatus;
  reportDesignVersion: number; // version of this report's report-design.json in S3; send it back as baseVersion when saving so a save built on a stale copy is rejected instead of overwriting someone else's. 0 = still the empty placeholder from create
  canRegenerate: boolean; // this deck is genuinely stuck, not merely unfinished: never generated, the survey layout is present, and no attempt is in flight. Computed server-side -- the client can't see whether the layout exists, which is what separates "still fetching from Decipher" (resolves itself) from "will never resolve"
  createdOn: string;
  updatedOn?: string;
  // The data this deck reads, carried on the report rather than fetched separately: two decks in a
  // project can read different sources, so this is per-report. Absent only on a report with no
  // source mapped, which is a broken state rather than a normal one.
  dataSourceId?: string;
  dataSourceName?: string;
  dataSourceProvider?: EndClientReportDataSourceProvider; // decides the shapes a data download is written in
  dataStatus?: EndClientReportStatus; // where the source's last populate run got to
  dataFetchedAt?: string; // last successful data.json write -- what "Data as of ..." shows
  // Counted when the data was pulled, since data.json is cond=qualified and no screen-out ever
  // reaches the browser. Two counts rather than a rate, so the formula can change without a re-pull.
  completesCount?: number;
  terminatesCount?: number; // overquota deliberately excluded -- they qualified
  dataError?: string;
  // NK-450: the theme this deck's colours came from. The colours themselves are in the design's
  // colorPalette; these say where they came from and whether the theme has moved on since.
  themeId?: string | null; // null/absent is OR Default
  themeName?: string | null; // null with a themeId set means the theme has since been deleted
  themeVersion?: number | null;
  themeOutOfDate?: boolean;
  // A share link that would open right now. Only the project's list fills it in.
  shareLinkActive?: boolean;
  /** That link, so the list can copy it. Only on the project's list, and only when it's active. */
  shareUrl?: string | null;
}

// PUT api/EndClientReport/{id}/design rejects a save for one of these reasons; the API sends the
// name in the error body's `reason` so the UI can tell "retry this" from "reload first" without
// matching on HTTP status codes. Mirrors Enums/DesignSaveResult.cs, minus its success member.
export type DesignSaveFailure =
  | 'Busy'            // 423 -- another save for this report is mid-flight; the same payload can be retried shortly
  | 'VersionConflict' // 409 -- someone else saved first; the local copy has to be reloaded before it can be saved
  | 'Invalid'         // 422 -- the design is structurally broken, so it was never written
  | 'ArchiveFailed'   // 500 -- the previous version couldn't be archived, so the save aborted rather than overwrite it
  | 'Unknown';

// A reusable report design owned by a client and shared across their projects. Structurally the
// same document a deck holds -- which is why the deck components edit both -- minus everything
// about survey data: a template needs no project and no ingestion to exist.
export interface EndClientReportTemplate {
  id: string;
  clientId: string;
  clientName?: string; // resolved server-side; the list shows who a template belongs to
  name: string;
  description?: string;
  templateVersion: number; // always 1 or above: a template is created with content. Send it back as baseVersion when saving
  // The project this template was authored against, and the survey the editor renders it with --
  // its variableMap holds that project's data positions, so no other survey resolves it correctly.
  // Null on templates created before this was recorded; those can't be edited with data.
  sourceProjectId?: string;
  createdOn: string;
  updatedOn?: string;
  // NK-450: same as EndClientReport's.
  themeId?: string | null;
  themeName?: string | null;
  themeVersion?: number | null;
  themeOutOfDate?: boolean;
}

export interface CreateEndClientReportTemplateRequest {
  name: string;
  description?: string;
  /** Who it belongs to. Internal users pick; a client user's own is used whatever they send. */
  clientId?: string;
  /** Alternative to clientId: the server resolves the client from this project. */
  projectId?: string;
  /** Seed the new template with a design — "save this report as a template", and cloning. */
  template?: ReportDesign;
  /** "Save as template": the report the design came from, whose uploaded pictures go with it. */
  sourceReportId?: string;
  /**
   * NK-450: the theme to start in; its current colours are applied over the content. Absent keeps
   * the content's colours and puts the template on OR Default.
   */
  theme?: ThemeChoice;
}

// ---------------------------------------------------------------------------------------------
// Themes (NK-450)
//
// A named set of report colours owned by a client. Reports and templates record which theme they
// use, but the colours are *copied* into each design's colorPalette -- editing a theme changes
// nothing until someone pushes the update out to the reports they pick.
// ---------------------------------------------------------------------------------------------

/** The stored theme document. Versioned so fonts, logos and so on can be added later. */
export interface ThemeDocument {
  $version: number;
  colorPalette: ColorPalette;
  /** The theme's logo (NK-518), copied onto a deck with the colours. Absent for no logo. */
  logo?: ReportLogo;
}

/** A logo uploaded through the theme editor, named by its id. Mirrors EndClientReportThemes.LogoKey. */
export interface ReportLogo {
  imageId: string;
}

export interface EndClientReportTheme {
  /** Null for OR Default, the built-in theme, which has no row. */
  id: string | null;
  clientId: string | null;
  name: string;
  description?: string;
  theme: ThemeDocument;
  /** Goes up only when the colours change. Null for OR Default, which never does. */
  themeVersion: number | null;
  /** OR Default: can't be edited or deleted, never has an update to push. */
  isDefault: boolean;
  reportCount: number;
  templateCount: number;
  createdOn?: string;
  updatedOn?: string;
  updatedByName?: string;
}

/**
 * A theme choice on a create or a design save. An object rather than a bare id so "no change" (no
 * object) and "OR Default" (themeId null) can be told apart.
 */
export interface ThemeChoice {
  themeId: string | null;
  /** Design saves: the version whose colours the editor applied. Ignored on create. */
  themeVersion?: number | null;
}

export interface CreateEndClientReportThemeRequest {
  name: string;
  description?: string;
  clientId?: string;
  projectId?: string;
  /** Absent starts from OR Default's colours. */
  theme?: ThemeDocument;
}

/** One report or template using a theme, as the apply dialog lists it. */
export interface EndClientReportThemeUsage {
  kind: 'report' | 'template';
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  themeVersion?: number | null;
  /** Behind the theme's current version, so applying the update would change it. */
  outOfDate: boolean;
  /** Reports only: has a live share link, so end clients see the change only after a redeploy. */
  published: boolean;
  /** Published reports: edited since publishing, so a republish sends those edits too. */
  hasUnpublishedEdits: boolean;
  /** Published reports: data refreshed since publishing, so a republish sends the newer data too. */
  hasNewerData: boolean;
}

export type ThemeApplyOutcome = 'Applied' | 'Busy' | 'Conflict' | 'NotFound' | 'Failed';

/** Started: the rebuild is running, and the old published copy serves until it lands. */
export type ThemeRepublishOutcome = 'Started' | 'NotPublished' | 'Failed';

export interface EndClientReportThemeApplyResult {
  kind: 'report' | 'template';
  id: string;
  name?: string;
  result: ThemeApplyOutcome;
  /** Only when a republish was asked for and the update applied. */
  republish?: ThemeRepublishOutcome | null;
}

/** Why a theme create/update was refused. Mirrors Enums/ThemeSaveResult.cs, minus Saved and InUse. */
export type ThemeSaveFailure = 'Invalid' | 'NameTaken' | 'NotFound' | 'Unknown';

// POST api/EndClientReport/{id}/regenerate refuses for one of these reasons. Every one of them
// means the client's view was out of date rather than something being broken, so re-reading the
// report is the response in each case. Mirrors Enums/DesignRegenerateResult.cs, minus Started.
export type DesignRegenerateFailure =
  | 'AlreadyGenerated' // 409 -- a design landed between the button rendering and the click
  | 'AttemptInFlight'  // 409 -- generation is already running for this report; wait
  | 'LayoutNotReady'   // 409 -- ingestion hasn't produced a layout yet, so there's nothing to build from
  | 'NotFound'         // 404 -- no such report in this instance
  | 'Unknown';

// One turn of the AI chat edit -- POST api/EndClientReport/{id}/design/chat. Note that a successful
// turn does not necessarily change anything: the agent answers questions and asks for
// clarification through this same shape, which is what `designChanged` distinguishes.
export interface DesignChatTurn {
  sessionId: string;       // mint-on-first-turn, sent back on every following turn; also what the agent's conversation memory is partitioned by, so a new id starts a fresh conversation against the same saved design
  message: string;         // the agent's prose, for the chat panel
  designChanged: boolean;  // false for an answered question or a clarification request -- don't re-read the design
  report?: EndClientReport; // present only when designChanged; carries the version the save landed on, which the next turn must send
}

// What the user has on screen when they send a chat turn, so "this page" and "here" resolve. The
// subsection by its section and its own id, and the screen of it by a block's `page` number (0 is
// the first). Mirrors EndClientReportChatViewingDto.
export interface DesignChatViewing {
  sectionId: string;
  subSectionId: string;
  page: number;
}

// Same principle as DesignSaveFailure: the API's `reason` names the case so the panel can tell
// "say this and let them retry" from "they have to reload". Mirrors Enums/DesignChatResult.cs,
// minus the members that are successes.
export type DesignChatFailure =
  | 'Rejected'        // 422 -- the edit couldn't be applied safely; nothing changed and rephrasing is the way forward
  | 'Busy'            // 423 -- a write to this design is mid-flight
  | 'VersionConflict' // 409 -- the deck moved on; reload before editing further
  | 'AgentFailed'     // 502 -- the assistant couldn't be reached, or said nothing usable
  | 'ArchiveFailed'   // 500 -- the edit was fine but the previous version couldn't be archived, so nothing was written
  | 'Unknown';

// Models/EndClientReportDataSource.cs -- one set of data (data.json / layout.json /
// layout-optimized.json) and the state of whatever last populated it. A project's own Decipher pull
// is one source; the same source can serve reports in more than one project.
export enum EndClientReportDataSourceKind {
  DecipherProject = 0,
  UploadedCsv = 1,
  UploadedExcel = 2,
  UploadedJson = 3,
}

// Whose data a source holds -- Enums/EndClientReportDataSourceProvider.cs: Decipher for a project's
// own pull, or whoever's export an upload holds. Separate from the kind, which is the file format:
// the same workbook from another platform needs a different parser. Only providers with a converter
// appear here; the picker offers "Other" as a dead end that explains itself rather than letting
// someone upload something nothing can read.
export enum EndClientReportDataSourceProvider {
  Decipher = 0,
}

export const END_CLIENT_REPORT_PROVIDER_LABELS: Record<EndClientReportDataSourceProvider, string> = {
  [EndClientReportDataSourceProvider.Decipher]: 'Decipher',
};

/**
 * How far a reporting domain has got through the DNS and certificate handshake that has to happen
 * before a shared report can be served on it — Enums/EndClientReportDomainStatus.cs (NK-399).
 *
 * Two values rather than a workflow: the intermediate stages aren't observable from Navigator, and a
 * status that has to be hand-updated to stay true mostly ends up false. Whoever is running it needs
 * one bit — can a link be created on this yet — and the notes for everything else.
 */
export enum EndClientReportDomainStatus {
  Pending = 0,
  Live = 1,
}

/**
 * The state of a report's published bundle — Enums/EndClientReportDeploymentStatus.cs (NK-399).
 *
 * There is no Expired value: expiry is derived from `expiresAt` when the link is read, because a
 * status column would stay wrong for as long as it took a sweep to notice, and "wrong" here means a
 * link that should have stopped working still does.
 */
export enum EndClientReportDeploymentStatus {
  Building = 0,
  Live = 1,
  Failed = 2,
  Revoked = 3,
}

/**
 * A report published as a read-only share link (NK-399). One per report, so re-keying revokes every
 * recipient at once.
 *
 * What is served is frozen at deploy: editing the deck or refreshing its data changes nothing a
 * client sees until someone re-deploys.
 */
export interface EndClientReportDeployment {
  id: string;
  reportId: string;
  domainId: string;
  host: string;
  /** The link to hand out. Both halves of the credential are in it — the id alone is not enough. */
  shareUrl: string;
  accessKey: string;
  keyIssuedAt: string;
  expiresAt?: string | null;
  /** Derived server-side at read time rather than stored. */
  expired: boolean;
  status: EndClientReportDeploymentStatus;
  lastError?: string | null;
  /** Null until a build has succeeded, which is how "is there anything to serve?" is answered. */
  deployedAt?: string | null;
  deployedBy: string;
  deployedDesignVersion?: number | null;
  deployedDataHash?: string | null;
}

/** A hostname a shared report can be served on (NK-399). */
export interface EndClientReportDomain {
  id: string;
  /** Null for the instance-wide fallback, which every client can publish on. */
  clientId?: string | null;
  /** Resolved on the management screen, which lists every client's domains. Absent on the deploy dialog's list. */
  clientName?: string | null;
  host: string;
  status: EndClientReportDomainStatus;
  notes?: string | null;
  shared: boolean;
}

/**
 * A file being uploaded, under the form field the API binds it to. An array because a format can
 * need more than one -- a CSV upload carries its layout separately, where an Excel export holds
 * both sheets in one workbook.
 */
export interface EndClientReportUploadFile {
  field: string;
  file: File;
}

/**
 * What a deck would lose by reading a different source, asked before the switch. Nothing is copied
 * into the design, so a variable the new source lacks renders as "No data for …" on the block —
 * which afterwards looks like a broken report rather than a mismatched survey.
 */
export interface EndClientReportDataSourcePreflight {
  variables: number;
  resolved: number;
  missing: string[]; // the design's own names for what wouldn't resolve
  unknown: boolean; // no datamap to check against, so "can't tell" rather than "all fine"
}

export interface EndClientReportUploadSlot {
  field: string; // the form field the API binds this file to
  label: string;
  accept: string;
}

export interface EndClientReportUploadProviderOption {
  provider: EndClientReportDataSourceProvider;
  label: string;
  /** Off until a converter exists for the pair. The upload form only offers what's on. */
  enabled: boolean;
  files: EndClientReportUploadSlot[];
}

export interface EndClientReportUploadFormatOption {
  kind: EndClientReportDataSourceKind;
  label: string;
  providers: EndClientReportUploadProviderOption[];
}

/**
 * What can be uploaded: a format, and the providers whose exports we can read in it. The form asks
 * for the format first and then narrows the providers, because the same platform's export in a
 * different format is a different parser -- the pair is what decides whether it can be read at all.
 *
 * **This table mirrors the backend's dispatch** in EndClientReportIngestionService.Convert, which is
 * the real authority: a pair enabled here and missing there fails the source after upload with "no
 * converter for a ... upload yet". Turning a pair on means both.
 */
export const END_CLIENT_REPORT_UPLOAD_FORMATS: EndClientReportUploadFormatOption[] = [
  {
    kind: EndClientReportDataSourceKind.UploadedExcel,
    label: 'Excel workbook (.xlsx)',
    providers: [
      {
        provider: EndClientReportDataSourceProvider.Decipher,
        label: 'Decipher',
        enabled: true,
        // One file: a Decipher Excel export carries the datamap as a second sheet.
        files: [{ field: 'file', label: 'Decipher export (.xlsx)', accept: '.xlsx,.xlsm' }],
      },
    ],
  },
  {
    kind: EndClientReportDataSourceKind.UploadedJson,
    label: 'JSON',
    providers: [
      {
        provider: EndClientReportDataSourceProvider.Decipher,
        label: 'Decipher',
        enabled: true,
        // Decipher's own API output, uploaded rather than pulled — so it goes through almost
        // untouched. Two files: the responses hold no survey structure.
        files: [
          { field: 'file', label: 'Responses (.json)', accept: '.json' },
          { field: 'layout', label: 'Datamap (.json)', accept: '.json' },
        ],
      },
    ],
  },
  {
    kind: EndClientReportDataSourceKind.UploadedCsv,
    label: 'CSV',
    providers: [
      {
        provider: EndClientReportDataSourceProvider.Decipher,
        label: 'Decipher',
        enabled: true,
        // Two files, unlike Excel: a CSV holds responses only. The layout is the *text* datamap
        // Decipher ships beside it in the same zip — not a second CSV, which is what this was
        // assumed to be before a real export settled it.
        files: [
          { field: 'file', label: 'Responses (.csv)', accept: '.csv' },
          { field: 'layout', label: 'Datamap (datamap.txt)', accept: '.txt' },
        ],
      },
    ],
  },
];

// One report reading a source, with the project it sits in. What the refresh dialog lists: a shared
// source reaches reports in other projects, and nothing else in the UI would show that.
export interface EndClientReportDataSourceUsage {
  reportId: string;
  reportName: string;
  projectId: string;
  projectName: string;
}

export interface EndClientReportDataSource {
  id: string;
  projectId?: string; // the project whose Decipher data this is; always absent for an upload
  ownerProjectId?: string; // the project it belongs to; only its reports can read it unless it's shared
  ownerProjectName?: string; // filled by the lists that say whose a source is
  isGlobal: boolean; // shared with the client's other projects
  kind: EndClientReportDataSourceKind;
  provider?: EndClientReportDataSourceProvider; // whose data it is; absent on sources from before pulls recorded it
  name: string;
  reportCount?: number; // live reports reading it; only the management list asks for this
  status: EndClientReportStatus; // same numeric values as EndClientReportIngestionStatus.cs
  startedAt: string;
  completedAt?: string;
  dataFetchedAt?: string;
  completesCount?: number;
  terminatesCount?: number;
  lastError?: string;
}

const SOURCE_KIND_LABELS: Record<EndClientReportDataSourceKind, string> = {
  [EndClientReportDataSourceKind.DecipherProject]: 'Survey',
  [EndClientReportDataSourceKind.UploadedCsv]: 'CSV upload',
  [EndClientReportDataSourceKind.UploadedExcel]: 'Excel upload',
  [EndClientReportDataSourceKind.UploadedJson]: 'JSON upload',
};

/** What a source is, as the lists show it: whose data, then how it arrived -- "Decipher · Excel upload". */
export function endClientReportSourceLabel(source: Pick<EndClientReportDataSource, 'kind' | 'provider'>): string {
  const kind = SOURCE_KIND_LABELS[source.kind] ?? 'Uploaded file';
  return source.provider === undefined || source.provider === null
    ? kind
    : `${END_CLIENT_REPORT_PROVIDER_LABELS[source.provider]} · ${kind}`;
}

// POST api/EndClientReport/{projectId} -- projectId is a route param, not part of the body.
export interface CreateEndClientReportRequest {
  name: string;
  description?: string;
  /**
   * Build the deck from this template: its slots are matched against the project's survey and the
   * design copied with the matches bound in. Absent with blank `guidance` means an empty deck.
   */
  templateId?: string;
  /**
   * Free-text description of what the report should cover, written for the generation agent. Lands
   * in the placeholder design's `generationGuidance` and is what the agent builds sections from.
   *
   * Blank is a supported mode, not an edge case -- but it's not a neutral default: with no guidance
   * the agent deliberately produces demographic filters and *nothing else*, on the reasoning that a
   * wrong guess at "which questions matter" costs the client more to undo than an empty deck costs
   * to fill in. So blank means "an empty deck to build yourself", which is why the create modal
   * says so rather than just marking this optional.
   */
  guidance?: string;
  /**
   * NK-450: the theme to build the deck in; its current colours are applied last, over whatever the
   * template or the generation agent chose. Absent: the template's theme with its colours as they
   * are, or OR Default.
   */
  theme?: ThemeChoice;
  /**
   * The source the deck reads: one of the project's own, or one its client shares. Absent means the
   * project's own survey, which only a Decipher project has -- the API refuses with
   * `reason: 'NoDataSource'` when there's nothing to build from.
   */
  dataSourceId?: string;
}

// report-design.json content — one file per REPORT (not per project; data.json/layout.json are the
// project-scoped ones), at {instanceId}/endclientreports/{projectId}/{reportId}.json. Prior versions
// go to {instanceId}/endclientreports/{projectId}/archive/{version}/{reportId}.json.
/**
 * What one unbound variable reference in a template is looking for. The key is the same key blocks
 * reference through `variableKey`/`variableKeys`/`groupBy`, so a template and a deck share one
 * document shape — a deck resolves those keys through `variableMap`, a template describes them here
 * and gets a real `variableMap` when it's applied to a study.
 */
export interface VariableSlot {
  description: string;  // the question wording this slot was built from; what the matcher reads
  shape: VariableShape; // constrains matching: a slot wanting multi-punch shouldn't bind to a single-select
  optionCount?: number; // roughly how many answers, so a matcher can prefer a like-for-like question
  /**
   * A sample of the answers, capped. Often a better signal than `description`, and reliably so for
   * the variables whose description is weakest: a hidden recode labelled "HIDDEN: Age recode" says
   * almost nothing, while "18-24, 25-34, 35-44…" says exactly what it measures. Truncated because
   * this rides in a prompt and a 40-option grid would earn none of that space.
   */
  optionLabels?: string[];
}

export type VariableShape = 'single-select' | 'multi-punch';

/**
 * One thing a template asked for that this survey couldn't provide, recorded on the deck built from
 * it. A to-do rather than a report: entries are removed as they're dealt with, so the list shrinks
 * rather than going stale when the survey data is refreshed.
 *
 * Navigator's own chrome only — never part of what a client sees. The renderer walks `sections`, and
 * nothing here should reach a deck.
 */
export interface UnresolvedSlot {
  slot: string; // the template's slot key
  why?: string; // what was looked for, and what the closest thing was
}

/** What caused a version of a design to be written. Mirrors NavigatorAPI's DesignSaveSource. */
export type DesignSaveSource =
  | 'Generated'
  | 'Manual'
  | 'ChatEdit'
  | 'TemplateApplied'
  | 'Regenerated'
  | 'Reverted'
  | 'Placeholder'
  | 'ThemeApplied'; // NK-450: a theme update pushed out from the theme

/**
 * One version in a deck's design history. What makes the S3 archive usable: versions are dense
 * integers so they can always be enumerated, but a list of numbers is no basis for choosing one.
 */
export interface DesignHistoryEntry {
  version: number;
  savedAt: string;
  savedBy?: string;
  /** Resolved server-side. Absent for a write no person made — generation, template apply. */
  savedByName?: string;
  source: DesignSaveSource;
  /** The enum's label, resolved server-side so this isn't reimplementing the mapping. */
  sourceLabel: string;
  /**
   * What changed, in one line: the agent's own summary for a chat edit, a computed structural diff
   * for a hand save. Absent when a save changed nothing recognisable, in which case `sourceLabel`
   * is what to show — never invent a description of a change we couldn't identify.
   */
  summary?: string;
  /** Chat edits only: what the user asked for, in their words. */
  userMessage?: string;
  sessionId?: string;
  /** The version the deck is on now: nothing to revert to, and its content is the live document. */
  isCurrent: boolean;
  /**
   * NK-450, decks only: the version the share link is serving. Not necessarily the current one --
   * edits since publishing sit above it until someone republishes.
   */
  isLive?: boolean;
}

export interface ReportDesign {
  colorPalette: ColorPalette;
  // The deck's logo, copied from its theme with the colours (NK-518) -- so it changes only when a
  // theme is picked or a theme update applied. Drawn in the top bar unless `hideLogo` is set, which is
  // the deck's own choice and survives a theme update. The API keeps a chat edit off `logo`.
  logo?: ReportLogo;
  hideLogo?: boolean;
  // Set when `logo` was uploaded for this deck rather than copied from its theme: theme updates then
  // leave the logo alone. Cleared by "Use theme's logo" and "Revert to theme" in the deck.
  usingCustomLogo?: boolean;
  variableMap: Record<string, string>; // FriendlyVariableName -> DataPosition; the only place components/filters reference layout.json's/data.json's raw shape
  sections: Record<string, ReportSection>; // keyed by Section id -- the left-nav groups
  sectionOrder: string[]; // ID-based: ordered list of keys into `sections`, defines left-nav group order
  filters: ReportFilters; // the right-hand filter rail
  generationGuidance?: string; // optional free-text guidance captured at report-creation time; embedded here so NavigatorAPI can hand it straight to the generation agent with no extra fetch. Blank/absent means "figure it out" mode.
  // Templates only: what each variable reference is looking for, so the same design can be re-bound
  // to a different survey's questions. A deck never carries it -- by then the references are bound
  // and `variableMap` is the answer. Written when a template's content is saved, from the layout it
  // was authored against (the browser has that; the server doesn't know which survey it came from).
  variableSlots?: Record<string, VariableSlot>;
  // Decks built from a template only: what the template asked for that this survey didn't have.
  // Written once when the template is applied, and cleared entry by entry as they're dealt with.
  unresolvedSlots?: UnresolvedSlot[];
  // Decks built from a template only: which template built this deck, and what version it was at.
  // Nothing reads them yet -- they're here so "a newer version of this template is available" is
  // answerable later from the deck in hand. In the document rather than on the row so they land in
  // the archive: version N of a deck records the template version behind it, permanently.
  // Declared so nothing rebuilding a design from scratch drops them; the API refuses to let a chat
  // edit touch them, and they're stripped when a deck is saved as a new template.
  templateId?: string;
  templateVersion?: number;
  // How every figure in the deck is worked out, as opposed to how any one module draws it. Report
  // level because a weight changes every number: a weighted table beside an unweighted chart of the
  // same question would put two different percentages on one page with nothing to say why.
  statistics?: ReportStatistics;
}

/** Defaults: unweighted, 95% confidence, a base under 30 flagged, whole percentages in tables. */
export interface ReportStatistics {
  /** Absent means unweighted. */
  weighting?: ReportWeighting;
  /** How sure a significance test has to be before a difference is marked. */
  confidenceLevel?: 90 | 95 | 99;
  /**
   * Hard threshold: columns answered by fewer respondents than this are greyed, left out of tests,
   * and cannot be drilled into. Always counted in respondents, never weighted.
   */
  minBase?: number;
  /** Soft threshold: columns under this many respondents carry a caution marker. */
  cautionBase?: number;
  /** Cells counting fewer respondents than this are suppressed. Absent or 0 is off. */
  minCellCount?: number;
  /** Correction for running many tests on one table. Absent is none, the banner-table convention. */
  correction?: 'none' | 'bonferroni' | 'bh';
  /** Decimal places on a table's percentages. */
  decimals?: 0 | 1 | 2;
}

/**
 * Where each respondent's weight comes from: a column already in the data, or worked out in the
 * browser by rim weighting the sample to target shares. Either way it is recomputed from the records
 * every time the deck loads, so the editor and a published link can never disagree about it.
 */
export type ReportWeighting =
  | { method: 'column'; column: string }
  | {
    method: 'targets';
    /** One entry per question weighted on, each answer's target share as a percentage. */
    targets: WeightTarget[];
    /** Caps on any one weight, so a handful of respondents cannot carry a subgroup. */
    minWeight?: number;
    maxWeight?: number;
  };

export interface WeightTarget {
  /** A `variableMap` key, so the question is packed into a published bundle with everything else. */
  variableKey: string;
  /** Keyed by answer text exactly as the datamap has it. Should total 100. */
  shares: Record<string, number>;
}

export interface ColorPalette {
  primary: string; // main brand/header color; default series color
  secondary: string; // supporting series color
  accent: string; // pop color for callouts, CTAs, sponsor-brand emphasis
  positive: string; // favorable/increase indicators (delta badges, promoters, momentum-up)
  negative: string; // unfavorable/decrease indicators (detractors, momentum-down)
  /**
   * The deck's text: body copy and headings on the white cards, and text on a fill light enough to
   * need dark ink. Optional, and never one of the chart colours above -- a deck saved before these
   * existed reads as it always did, from DEFAULT_TEXT_COLORS.
   */
  darkText?: string;
  /** Text on a fill dark enough to need light ink: banners, stage headers, filled buttons, chips. */
  lightText?: string;
}

/**
 * The text colours a palette without its own uses: a slate rather than near-black -- the shade the
 * key takeaway and the index already used, softer on a white card than black -- and white.
 */
export const DEFAULT_TEXT_COLORS: Required<Pick<ColorPalette, 'darkText' | 'lightText'>> = {
  darkText: '#26414c',
  lightText: '#ffffff',
};

/**
 * What a deck looks like before anyone chooses otherwise: the palette of the prototype this
 * feature was built from, which the client signed off on.
 *
 * The five must stay five *distinct* colours. `chartColors()` builds its no-repeat ramp by
 * walking these roles and then tinting/shading each one, so two roles sharing a hex would make
 * two series in the same chart indistinguishable. That is why `negative` is a red of its own
 * rather than reusing `accent`'s orange, which is what the prototype's own falling-delta badge
 * happened to use.
 *
 * A stored design always carries its own `colorPalette`, so this is only the fallback for the
 * moment before one has loaded. The value new decks actually start from is written server-side
 * (EndClientReportDesignDefaults.BuildEmptyDesignJson) and by the generation agent.
 */
export const DEFAULT_COLOR_PALETTE: ColorPalette = {
  primary: '#13455B',
  secondary: '#1F7A8C',
  accent: '#C4662B',
  positive: '#3B7A57',
  negative: '#a23b2b',
};

export interface ReportSection {
  id: string; // stable id; key in ReportDesign.sections; used for AI edit targeting (rename/delete/reorder)
  name: string; // nav group header text
  pages: Record<string, ReportSubSection>; // keyed by SubSection id -- see the note on ReportSubSection
  pageOrder: string[]; // ID-based: ordered list of keys into `pages`, defines nav order within this Section
}

/**
 * One entry in a Section's nav list. The deck's three levels are Section -> SubSection -> Page.
 *
 * **Its persisted keys still say "page"** -- `pages`, `pageOrder`, `pageSections`,
 * `pageSectionOrder` -- because this level was called a Page until the vocabulary was settled, and
 * the document shape is not the frontend's to change alone. It is read and validated by
 * `EndClientReportDesignValidator`, rewritten by `EndClientReportDesignMerge`, summarised by
 * `EndClientReportDesignDiff`, walked by `EndClientReportTemplateApply`, and mirrored by hand in
 * the agent's zod schema and its edit prompt -- none of which share a source of truth. Renaming the
 * keys is a four-way coordinated release plus a migration of every stored deck, archived version,
 * template and published bundle, for no behaviour.
 *
 * So: the names in this file and in the UI are the vocabulary; the keys in the JSON are history.
 * This comment is the mapping. Do not rename `pages`/`pageSections` without Jonathan.
 */
export interface ReportSubSection {
  id: string; // stable id; key in ReportSection.pages
  name: string; // subsection title
  subheader?: string; // optional subheader under the title
  // The one-line "so what" for this subsection, drawn as a callout above its blocks. Optional and
  // additive: a design without it renders exactly as before, and the backend validator checks
  // only the required root keys, so no stored deck needs migrating.
  keyTakeaway?: string;
  pageSections: Record<string, PageSection>; // keyed by PageSection id -- the individual chart/report blocks on this page
  pageSectionOrder: string[]; // ID-based: ordered list of keys into `pageSections`; order matters within a segmentLocation (e.g. two 'left' blocks stack in this order)
}

export type SegmentLocation = 'full' | 'left' | 'right'; // where this block sits on its page; will grow (e.g. grid coords) later

export interface PageSectionBase {
  id: string; // stable id; key in ReportSubSection.pageSections; used for AI edit targeting (rename/delete/reorder/chart-type swap)
  header?: string; // optional title shown above this specific chart block (distinct from the SubSection's own name/subheader)
  segmentLocation: SegmentLocation;
  /**
   * Which page of its SubSection this block is drawn on. Absent means the first, so a design that
   * has never heard of pages is a one-page design -- which is every deck, template and archived
   * version written before this existed, and every design the generation agent writes today.
   *
   * A page is an attribute of the block rather than a level of nesting, deliberately. Nesting would
   * have meant a new node in a document that four separate layers walk by string key, and the two
   * gates that could reject it -- the validator's block check and
   * `EndClientReportDesignMerge.BlockRequired` -- both name the fields they *require* and ignore the
   * rest. So this is additive the same way `keyTakeaway` was: nothing to migrate, and the agent
   * needs no schema change to keep producing valid designs.
   *
   * Blocks sharing a number are drawn together, in `pageSectionOrder` order.
   */
  page?: number;
}

// One entry per report type. Adding a module means one line here plus one new file in
// end-client-reporting-component-types/ -- nothing else in this file changes.
export interface ReportTypeDataMap {
  'pie-chart': PieChartExtraData;
  'bar-chart': BarChartExtraData;
  'filter-comparison': FilterComparisonExtraData;
  'donut-chart': DonutChartExtraData;
  'study-overview': StudyOverviewExtraData;
  'headline-stats': HeadlineStatsExtraData;
  'kpi-score': KpiScoreExtraData;
  'action-list': ActionListExtraData;
  'stacked-scale': StackedScaleExtraData;
  'funnel': FunnelExtraData;
  'segment-tiles': SegmentTilesExtraData;
  'subgroup-panels': SubgroupPanelsExtraData;
  'crosstab': CrosstabExtraData;
  'diverging-bars': DivergingBarsExtraData;
  'tiered-ranking': TieredRankingExtraData;
  'open-end-explorer': OpenEndExplorerExtraData;
  'word-cloud': WordCloudExtraData;
  'heatmap': HeatmapExtraData;
  'text': TextExtraData;
  'image': ImageExtraData;
  'scatter-plot': ScatterPlotExtraData;
  'ownership-matrix': OwnershipMatrixExtraData;
  'stage-matrix': StageMatrixExtraData;
  'stage-explainer': StageExplainerExtraData;
}

export type ReportType = keyof ReportTypeDataMap;

// Generates the discriminated union automatically from ReportTypeDataMap above,
// so the union itself never needs hand-editing as report types are added.
export type PageSection = {
  [K in ReportType]: PageSectionBase & { reportType: K; reportExtraData: ReportTypeDataMap[K] };
}[ReportType];

export interface ReportFilters {
  compareBy: string[]; // VariableMap keys usable for the "CompareBy" breakout dropdown
  filterableValues: string[]; // VariableMap keys usable in the filter rail
}
